// =====================================================
// Community Card - Display Community Details with Photo Carousel
// =====================================================

import React, { useState, useEffect, useCallback } from 'react';
import { CommunityCard as CommunityCardType } from '../../types';

interface CommunityCardProps {
  card: CommunityCardType;
}

const AUTOPLAY_INTERVAL = 4000; // 4 seconds between slides

const CommunityCard: React.FC<CommunityCardProps> = ({ card }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [validImages, setValidImages] = useState<string[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Get images array (use images if available, fallback to single image_url)
  const sourceImages = card.images && card.images.length > 0
    ? card.images
    : (card.image_url ? [card.image_url] : []);

  // Filter out broken/invalid images on mount
  useEffect(() => {
    const checkImages = async () => {
      const validUrls: string[] = [];

      for (const url of sourceImages) {
        // Skip empty, null, undefined, or obviously invalid URLs
        if (!url || typeof url !== 'string' || url.trim() === '' || url === 'null' || url === 'undefined') {
          continue;
        }

        // Skip URLs that don't look valid (should start with http, https, or /)
        const trimmedUrl = url.trim();
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://') && !trimmedUrl.startsWith('/')) {
          continue;
        }

        try {
          const isValid = await new Promise<boolean>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = trimmedUrl;
            // Timeout after 5 seconds
            setTimeout(() => resolve(false), 5000);
          });

          if (isValid) {
            validUrls.push(trimmedUrl);
          }
        } catch {
          // Skip broken images
        }
      }

      // If no valid images, use placeholder
      if (validUrls.length === 0) {
        validUrls.push('https://via.placeholder.com/400x300?text=Community+Image');
      }

      setValidImages(validUrls);
      setImagesLoaded(true);
    };

    checkImages();
  }, [sourceImages.join(',')]); // Join to create stable dependency

  // Autoplay functionality
  useEffect(() => {
    if (!imagesLoaded || validImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev < validImages.length - 1 ? prev + 1 : 0
      );
    }, AUTOPLAY_INTERVAL);

    return () => clearInterval(interval);
  }, [imagesLoaded, validImages.length]);

  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : validImages.length - 1
    );
  }, [validImages.length]);

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev < validImages.length - 1 ? prev + 1 : 0
    );
  }, [validImages.length]);

  // Handle action button clicks (Floor Plans, Quick Move-Ins)
  const handleActionClick = (action?: string) => {
    if (action) {
      // Dispatch custom event for chatbot actions
      const event = new CustomEvent('chatbot-action', {
        detail: { action }
      });
      window.dispatchEvent(event);
    }
  };

  // Format description: split into readable paragraphs and bullet points
  const formatDescription = (description: string) => {
    // Check if description has a "features:" pattern
    const featuresMatch = description.match(/^([\s\S]*?features:)\s*/i);

    if (featuresMatch) {
      // Has features - split into intro and bullet points
      const introText = featuresMatch[1];
      const featuresText = description.substring(featuresMatch[0].length);

      const introParagraphs = introText
        .replace(/([.!?])([A-Z])/g, '$1|||$2')
        .split('|||')
        .map(p => p.trim())
        .filter(p => p.length > 0);

      const features = featuresText
        .replace(/([a-z])([A-Z])/g, '$1|||$2')
        .split('|||')
        .map(f => f.trim())
        .filter(f => f.length > 0);

      return { introParagraphs, features };
    }

    // No features pattern - split into readable paragraphs
    // Split on sentence boundaries and group every 2-3 sentences
    const sentences = description
      .replace(/([.!?])\s+/g, '$1|||')
      .split('|||')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    // Group sentences into paragraphs (2-3 sentences each)
    const introParagraphs: string[] = [];
    let currentParagraph: string[] = [];

    sentences.forEach((sentence, index) => {
      currentParagraph.push(sentence);

      // Start new paragraph after 2-3 sentences or at natural break points
      const isNaturalBreak = sentence.endsWith('!') ||
        sentence.includes('Contact us') ||
        sentence.includes('Find your') ||
        sentence.includes('Get up to');

      if (currentParagraph.length >= 2 && (isNaturalBreak || currentParagraph.length >= 3)) {
        introParagraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
    });

    // Add remaining sentences
    if (currentParagraph.length > 0) {
      introParagraphs.push(currentParagraph.join(' '));
    }

    return { introParagraphs, features: [] };
  };

  // Show placeholder while checking images
  const displayImages = imagesLoaded ? validImages : sourceImages;

  return (
    <div className="chat-card animate-slide-up">
      {/* Photo Carousel */}
      <div className="relative h-48 bg-gray-200">
        <img
          src={displayImages[currentImageIndex] || 'https://via.placeholder.com/400x300?text=Community+Image'}
          alt={`${card.community_name} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300"
          onError={(e) => {
            e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Community+Image';
          }}
        />

        {/* Navigation Arrows - only show if multiple images */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white
                         p-1.5 rounded-full shadow-lg transition-all"
              aria-label="Previous image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white
                         p-1.5 rounded-full shadow-lg transition-all"
              aria-label="Next image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Image Counter Dots - only show if multiple images */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5">
            {displayImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-white w-4'
                    : 'bg-white/60 hover:bg-white/80'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-4">
        <h3 className="font-bold text-lg text-gray-900 mb-1">
          {card.community_name}
        </h3>
        {card.region_name && (
          <p className="text-sm text-gray-600 mb-2">
            {card.region_name}
          </p>
        )}

        {/* Specs + Price Row */}
        {(card.specs || card.starting_price) && (
          <div className="flex gap-2 mb-3 py-2 border-t border-b border-gray-100">
            {/* Left Side - Specs in 2x2 Grid */}
            {card.specs && (
              <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-700">
                {/* Row 1, Col 1: Bed */}
                {(card.specs.minBed !== undefined || card.specs.maxBed !== undefined) && (
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span>
                      {card.specs.minBed === card.specs.maxBed
                        ? card.specs.minBed
                        : `${card.specs.minBed}–${card.specs.maxBed}`} bed
                    </span>
                  </div>
                )}
                {/* Row 1, Col 2: Bath */}
                {(card.specs.minBath !== undefined || card.specs.maxBath !== undefined) && (
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                    </svg>
                    <span>
                      {card.specs.minBath === card.specs.maxBath
                        ? card.specs.minBath
                        : `${card.specs.minBath}–${card.specs.maxBath}`} bath
                    </span>
                  </div>
                )}
                {/* Row 2, Col 1: Garage */}
                {(card.specs.minGarage !== undefined || card.specs.maxGarage !== undefined) && (
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>
                      {card.specs.minGarage === card.specs.maxGarage
                        ? card.specs.minGarage
                        : `${card.specs.minGarage}–${card.specs.maxGarage}`}-car
                    </span>
                  </div>
                )}
                {/* Row 2, Col 2: Sqft */}
                {(card.specs.minSqFt !== undefined || card.specs.maxSqFt !== undefined) && (
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                    <span>
                      {card.specs.minSqFt === card.specs.maxSqFt
                        ? card.specs.minSqFt?.toLocaleString()
                        : `${card.specs.minSqFt?.toLocaleString()}–${card.specs.maxSqFt?.toLocaleString()}`} sqft
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Right Side - Starting Price */}
            {card.starting_price !== undefined && (
              <div className="flex-1 text-right flex flex-col justify-center">
                <span className="text-xs text-gray-500">Starting From</span>
                <span className="text-lg font-bold text-betenbough-olive">
                  ${card.starting_price.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Description - Expandable with formatted bullets */}
        {card.description && (() => {
          const { introParagraphs, features } = formatDescription(card.description);
          const hasFeatures = features.length > 0;
          const truncateLength = 55;
          const shouldTruncate = card.description.length > truncateLength;

          return (
            <div className="mb-3">
              {!isDescriptionExpanded ? (
                // Collapsed: show 2 lines with "Read more" at end
                <div className="relative">
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {card.description}
                  </p>
                  {shouldTruncate && (
                    <button
                      onClick={() => setIsDescriptionExpanded(true)}
                      className="absolute bottom-0 right-0 text-sm text-betenbough-olive hover:underline bg-white pl-1"
                    >
                      ... Read more
                    </button>
                  )}
                </div>
              ) : (
                // Expanded: show formatted with paragraphs and bullets
                <>
                  <div className="text-sm text-gray-700 space-y-2">
                    {introParagraphs.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                    {hasFeatures && (
                      <ul className="list-disc list-inside space-y-0.5">
                        {features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    onClick={() => setIsDescriptionExpanded(false)}
                    className="text-xs text-betenbough-olive hover:underline mt-1"
                  >
                    Show less
                  </button>
                </>
              )}
            </div>
          );
        })()}

        {/* Stats + Action Buttons - Combined for alignment */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          {/* Floor Plans Column - only show if floor plans exist */}
          {card.total_floor_plans !== undefined && card.total_floor_plans > 0 && (
            <div className="flex flex-col">
              <div className="text-sm mb-2">
                <span className="text-gray-600">Floor Plans:</span>
                <span className="ml-1 font-semibold text-betenbough-olive">
                  {card.total_floor_plans}
                </span>
              </div>
              <button
                onClick={() => handleActionClick(card.floor_plans_action)}
                className="text-center border border-betenbough-olive text-betenbough-olive hover:bg-betenbough-olive/10
                           px-2 py-1.5 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
              >
                Floor Plans
              </button>
            </div>
          )}
          {/* Quick Move-In Column - only show if QMI homes exist */}
          {card.total_qmi_homes !== undefined && card.total_qmi_homes > 0 && (
            <div className="flex flex-col">
              <div className="text-sm mb-2">
                <span className="text-gray-600">Quick Move-In:</span>
                <span className="ml-1 font-semibold text-betenbough-olive">
                  {card.total_qmi_homes}
                </span>
              </div>
              <button
                onClick={() => handleActionClick(card.qmi_homes_action)}
                className="text-center bg-betenbough-olive hover:bg-betenbough-olive/90 text-white
                           px-2 py-1.5 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
              >
                Quick Move-Ins
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityCard;
